/** @jsxImportSource @emotion/react */
import { css, CSSObject } from "@emotion/react";

import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router";

import { FILTER_NAMES } from "@/components/Filters/types";
import { useNavigationStore } from "@/stores/useNavigationStore";
import {
  useDriversTableStore,
  useVehiclesTableStore
} from "@/stores/useTableStore";
import { useUserDataStore } from "@/stores/useUserDataStore";
import Typography from "@mui/material/Typography";

import useChangeSelectedCompany from "../../../../../components/Navbar/components/CompanySelector/hooks/useChangeSelectedCompany";
import {
  getDetailsDataValues,
  ROUTES,
  TABLE_NAMES
} from "../../../../../shared";
import { StatusCompanyDto, StatusSummaryType } from "../types";

const useDeviceStatusFunctions = () => {
  const { changeSelectedCompany, handleCloseDesktopDetails } =
    useChangeSelectedCompany();

  const setVehicleParams = useVehiclesTableStore(state => state.setParams);
  const setDriverParams = useDriversTableStore(state => state.setParams);

  const resetVehicleParams = useVehiclesTableStore(
    state => state.resetExceptPaginationAndColumns
  );

  const resetDriverParams = useDriversTableStore(
    state => state.resetExceptPaginationAndColumns
  );

  const navigate = useNavigate();

  const { t } = useTranslation();

  const detailsDataFromTableName = (infoData: StatusSummaryType) => {
    return getDetailsDataValues(
      TABLE_NAMES.Vehicles,
      infoData.name,
      infoData.name,
      true
    );
  };

  const resourceDownloadFilterDetails = (
    infoData: StatusSummaryType,
    route: ROUTES
  ) => {
    const filterValueObject = {
      id: infoData.name,
      name: infoData.name
    };

    return {
      filterValue: filterValueObject,
      pageLocation: route,
      filterName: FILTER_NAMES.LastDownload
    };
  };

  const handleStatusInfoListItemClick = async (
    itemData: StatusCompanyDto,
    infoData: StatusSummaryType
  ) => {
    resetVehicleParams();
    useNavigationStore.getState().setTargetRoute(ROUTES.Vehicles);

    const { filterName, filterValue } = detailsDataFromTableName(infoData);

    const newFilterParams = {
      [filterName]: [filterValue]
    };

    await changeSelectedCompany(itemData.id, false, ROUTES.Vehicles);

    setVehicleParams({
      filters: newFilterParams
    });

    handleCloseDesktopDetails();
  };

  const handleResourceDownloadClick = async (
    itemData: StatusCompanyDto,
    infoData: StatusSummaryType,
    route: ROUTES
  ) => {
    const isDriverRoute = route === ROUTES.Drivers;
    const resetParams = isDriverRoute ? resetDriverParams : resetVehicleParams;
    const setParams = isDriverRoute ? setDriverParams : setVehicleParams;

    resetParams();

    const user = useUserDataStore.getState().userData;
    const { setTargetRoute } = useNavigationStore.getState();

    const { filterName, filterValue } = resourceDownloadFilterDetails(
      infoData,
      route
    );

    const newFilterParams = {
      [filterName]: [filterValue]
    };

    if (itemData.id === user?.selectedCompany.id) {
      navigate(route);
    } else {
      try {
        setTargetRoute(route);

        await changeSelectedCompany(itemData.id, false, route);
      } finally {
        setTargetRoute(null);
      }
    }

    setParams({
      filters: newFilterParams
    });

    handleCloseDesktopDetails();
  };

  const renderEmptyState = (divWithCenteredContent: CSSObject) => (
    <div css={css(divWithCenteredContent)}>
      <Typography variant="body1Medium">
        {t("Dashboard##no results")}
      </Typography>
    </div>
  );

  return {
    renderEmptyState,
    handleStatusInfoListItemClick,
    handleResourceDownloadClick
  };
};

export default useDeviceStatusFunctions;
